<template>
  <Combobox as="div" v-model="model" @update:modelValue="clearKeywords" nullable>
    <ComboboxLabel :for='labelId' class="block text-sm/6 font-medium text-gray-900 mb-2" v-if='!props.hideLabel' :class='[props.srLabel ? "sr-only" : ""]'>{{ props.label }}</ComboboxLabel>
    <div class="relative">
      <ComboboxInput :id='labelId' class="w-full rounded-md border-0 bg-white py-1.5 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-jananavy-900 sm:text-sm/6" @change="setKeywords" @blur="clearKeywords" :display-value="getDisplayValue" @click='click' :placeholder='props.placeholder' ref='input' />
      <ComboboxButton class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none pointer-events-none" ref='button'>
        <ChevronUpDownIcon class="h-5 w-5 text-gray-400" aria-hidden="true" v-if='!query.isLoading()' />
        <LoadingIcon class='h-5 w-5 text-gray-400' aria-hidden="true" v-if='query.isLoading()' />
      </ComboboxButton>
      <ComboboxOptions class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
        <div v-if="!query.hasItems() && hasKeywords()" class="relative cursor-default select-none px-4 py-2 text-gray-700">
          Nothing found.
        </div>
        <ComboboxOption v-for="item in query.getItems()" :key="collection.getId(item)" :value="item" as="template" v-slot="{ active, selected }">
          <li :class="['relative cursor-default select-none py-2 pl-8 pr-4', active ? 'bg-jananavy-900 text-white' : 'text-gray-900']">
            <span :class="['block truncate', selected && 'font-semibold']">
              {{ collection.getName(item) }}
            </span>
            <span v-if="selected" :class="['absolute inset-y-0 left-0 flex items-center pl-1.5', active ? 'text-white' : 'text-jananavy-900']">
              <CheckIcon class="h-5 w-5" aria-hidden="true" />
            </span>
          </li>
        </ComboboxOption>
      </ComboboxOptions>
    </div>
  </Combobox>
</template>

<script setup>

import { nanoid } from 'nanoid';

import { ref, computed, reactive } from 'vue';
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxLabel,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/vue';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/vue/20/solid';

import { usePayloadQuery } from '@/kernel/api';
import { getCollection } from '@/kernel/collections';
import { debounce } from '@/kernel/debounce';

import LoadingIcon from '@/components/icons/loading-icon.vue';

const props = defineProps({
  collection: String,
  label: String,
  hideLabel: Boolean,
  srLabel: Boolean,
  placeholder: {
    type: String,
    default: 'Search...'
  },
  labelId: String,
  filters: Object
});

const collection = getCollection(props.collection);

const model = defineModel();

const input = ref();
const button = ref();

const query = usePayloadQuery(collection.getApiIndexEndpoint(), {
  sort: collection.getDefaultSort(),
  contexts: collection.getIndexContextFilters(),
  filters: props.filters || null
});

const setKeywords = (e) => {
  query.setSearch(collection.getSearchKeys(), e.target.value);
}

const clearKeywords = () => {
  query.clearSearch();
}

const hasKeywords = () => {
  return query.hasSearch();
}

const getDisplayValue = (item) => {
  return item ? collection.getName(item) : '';
}

const click = () => {
  button.value?.el?.click();
}

const labelId = computed(() => {
  return props.labelId || nanoid();
});

query.doQuery();

</script>
